import React, { useState, useRef, useEffect } from 'react';
import { auth, db } from './firebase';
import { updateProfile, updatePassword } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { isAdminUser } from './adminConfig';
import ResetData from './ResetData';

// Shrinks the picked image client-side into a small base64 data URL
const resizeImage = (file, maxSize = 300) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const Settings = () => {
  const user = auth.currentUser;
  const fileInputRef = useRef(null);

  // States
  const [name, setName] = useState(user?.displayName || '');
  const [photo, setPhoto] = useState(user?.photoURL || '');
  const [pass, setPass] = useState('');
  const [load, setLoad] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Photos live in Firestore, not Firebase Auth's photoURL field (too short
  // to hold an uploaded image), so load whatever was saved there before.
  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "userProfiles", user.uid)).then((snap) => {
      if (snap.exists() && snap.data().photoURL) setPhoto(snap.data().photoURL);
    });
  }, [user]);

  // Resizes the picked image and previews it; saving happens on "Save Profile"
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert("Please choose an image file.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await resizeImage(file);
      setPhoto(dataUrl);
    } catch (error) {
      alert("Couldn't read that image: " + error.message);
    }
    setUploading(false);
  };

  // Profile Update Function
  const saveProfile = async (e) => {
    e.preventDefault();
    setLoad(true);
    try {
      await updateProfile(user, { displayName: name });
      await setDoc(doc(db, "userProfiles", user.uid), {
        photoURL: photo,
        updatedAt: serverTimestamp()
      }, { merge: true });
      alert("✅ Profile Updated Successfully!");
      window.location.reload();
    } catch (error) {
      alert("Error: " + error.message);
    }
    setLoad(false);
  };

  // Password Update Function
  const savePass = async (e) => {
    e.preventDefault();
    if (pass.length < 6) return alert("Password must be at least 6 characters!");
    
    try {
      await updatePassword(user, pass);
      alert("✅ Password Changed!");
      setPass('');
    } catch (error) {
      alert("Error: " + error.message);
    }
  };

  // Helper to handle broken images
  const handleImageError = (e) => {
    e.target.src = `https://ui-avatars.com/api/?name=${name || 'User'}&background=4a00e0&color=fff&size=128`;
  };

  return (
    <div style={{ display: 'flex', gap: '30px', padding: '20px', flexWrap: 'wrap' }}>
      
      {/* Left Card: Profile Settings */}
      <div style={{ 
        flex: 1, 
        minWidth: '350px',
        background: '#fff', 
        padding: '40px 30px', 
        borderRadius: '25px', 
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        textAlign: 'center' 
      }}>
        <h3 style={{ marginBottom: '20px', color: '#333' }}>Profile Settings</h3>
        
        {/* Profile Image Preview */}
        <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto 30px' }}>
          <img 
            src={photo || `https://ui-avatars.com/api/?name=${name}&background=random`} 
            onError={handleImageError}
            style={{
              width: '100%', 
              height: '100%', 
              borderRadius: '50%', 
              objectFit: 'cover',
              border: '4px solid #f0f0f0'
            }} 
            alt="profile"
          />
        </div>

        <form onSubmit={saveProfile}>
          <div style={{ textAlign: 'left', marginBottom: '15px' }}>
            <label style={{ fontSize: '13px', color: '#666', marginLeft: '10px' }}>Full Name</label>
            <input 
              value={name} 
              onChange={e => setName(e.target.value)} 
              placeholder="Enter your name" 
              style={{
                width: '100%', padding: '12px 15px', borderRadius: '12px', 
                border: '1px solid #ddd', marginTop: '5px', fontSize: '15px'
              }}
            />
          </div>

          <div style={{ textAlign: 'left', marginBottom: '25px' }}>
            <label style={{ fontSize: '13px', color: '#666', marginLeft: '10px' }}>Photo URL (Direct Link)</label>
            <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
              <input
                value={photo}
                onChange={e => setPhoto(e.target.value)}
                placeholder="https://example.com/my-photo.jpg"
                style={{
                  flex: 1, padding: '12px 15px', borderRadius: '12px',
                  border: '1px solid #ddd', fontSize: '15px'
                }}
              />
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current.click()}
                style={{
                  padding: '0 18px', borderRadius: '12px', border: '1px solid #ddd',
                  background: '#f5f3ff', color: '#4a00e0', fontWeight: 'bold',
                  fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap',
                  opacity: uploading ? 0.7 : 1
                }}
              >
                {uploading ? "Uploading..." : "Upload from PC"}
              </button>
            </div>
            <small style={{ color: '#999', display: 'block', marginTop: '5px', fontSize: '11px' }}>
              * Paste a link above, or upload a photo from your device
            </small>
          </div>

          <button 
            type="submit" 
            disabled={load}
            style={{
              width: '100%', padding: '14px', background: '#4a00e0', 
              color: '#fff', border: 'none', borderRadius: '15px', 
              fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
              transition: '0.3s', opacity: load ? 0.7 : 1
            }}
          >
            {load ? "Updating..." : "Save Profile"}
          </button>
        </form>
      </div>

      {/* Right Card: Security */}
      <div style={{ 
        flex: 1, 
        minWidth: '350px',
        background: '#fff', 
        padding: '40px 30px', 
        borderRadius: '25px', 
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)' 
      }}>
        <h3 style={{ marginBottom: '20px', color: '#333' }}>Security</h3>
        <p style={{ color: '#777', fontSize: '14px', marginBottom: '25px' }}>
          Change your account password to keep it secure.
        </p>

        <form onSubmit={savePass}>
          <div style={{ textAlign: 'left', marginBottom: '25px' }}>
            <label style={{ fontSize: '13px', color: '#666', marginLeft: '10px' }}>New Password</label>
            <input 
              type="password" 
              value={pass} 
              onChange={e => setPass(e.target.value)} 
              placeholder="Enter new password" 
              style={{
                width: '100%', padding: '12px 15px', borderRadius: '12px', 
                border: '1px solid #ddd', marginTop: '5px', fontSize: '15px'
              }}
            />
          </div>

          <button 
            type="submit" 
            style={{
              width: '100%', padding: '14px', background: '#ff4d4d', 
              color: '#fff', border: 'none', borderRadius: '15px', 
              fontSize: '16px', fontWeight: 'bold', cursor: 'pointer'
            }}
          >
            Change Password
          </button>
        </form>
      </div>

      {/* পুরো ডেটা মোছার টুল -- শুধু মূল Admin লগইনে দেখা যায় */}
      {isAdminUser(user) && <ResetData />}

    </div>
  );
};

export default Settings;